import React, { useEffect, useRef, useState } from "react";
import choose1 from "../assets/choose1.png";
import choose2 from "../assets/choose2.png";
import choose3 from "../assets/choose3.png";
import {
  Clock3,
  ShieldCheck,
  Paperclip,
  Shapes,
  BadgeCheck,
  Layers3,
} from "lucide-react";
import Hero from "./home/Hero";
import Featured from "./home/Featured";
import ProductGrid from "./home/ProductGrid";
import WhyBuySection from "./home/WhyBuySection";
import SEO from "./SEO";

const TextCard = ({ title, text, Icon }) => {
  return (
    <div className="group flex min-h-[148px] flex-col items-center justify-start rounded-[10px] border border-[#e5eaf0] bg-[#f8fafc] px-5 py-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[#f97316]/40 hover:bg-white hover:shadow-[0_16px_35px_rgba(15,23,42,0.12)] sm:px-6 lg:min-h-[160px] lg:px-7">
      <div className="mb-3 flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-[#f97316]/10 text-[#f97316] transition-all duration-300 group-hover:bg-[#f97316] group-hover:text-white sm:h-[46px] sm:w-[46px]">
        <Icon
          className="h-[23px] w-[23px] sm:h-[25px] sm:w-[25px]"
          strokeWidth={2.3}
        />
      </div>

      <span className="mb-2 block text-[17px] font-bold leading-[1.25] text-[#0a2336] sm:mb-[10px] lg:text-[16px]">
        {title}
      </span>

      <span className="mb-0 block max-w-[470px] text-[13px] font-medium leading-[21px] text-[#415164] sm:text-[14px] sm:leading-[22px]">
        {text}
      </span>
    </div>
  );
};

const WhyImageCard = ({ item }) => {
  return (
    <div className="group mx-auto block h-full w-full max-w-[280px] overflow-hidden rounded-[15px] border border-[#d9d9d9] bg-[#f3f3f3] px-3 pb-4 pt-3 text-center shadow-[0_4px_16px_rgba(0,0,0,0.16)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_26px_rgba(0,0,0,0.22)] sm:max-w-[290px] sm:px-4 sm:pb-5 sm:pt-4">
      <div className="mx-auto flex h-[118px] w-full items-center justify-center overflow-hidden rounded-[12px] bg-transparent sm:h-[180px] lg:h-[195px]">
        <img
          src={item.src}
          alt={item.alt}
          className="max-h-full max-w-full object-contain transition-transform duration-500 ease-out group-hover:scale-105"
        />
      </div>

      <h3 className="mt-4 line-clamp-2 min-h-[38px] text-center text-[13px] font-extrabold leading-[19px] text-black sm:min-h-[44px] sm:text-[16px] sm:leading-[22px] lg:text-[17px]">
        {item.title}
      </h3>

      <p className="mx-auto mt-2 line-clamp-2 text-center text-[12px] font-medium leading-[20px] text-[#111827] sm:text-[14px] sm:leading-[22px]">
        {item.description}
      </p>
    </div>
  );
};

const MobileCardSlider = ({ cards }) => {
  const [activeCard, setActiveCard] = useState(0);
  const [incomingCard, setIncomingCard] = useState(null);
  const [direction, setDirection] = useState("next");
  const [isSliding, setIsSliding] = useState(false);
  const timerRef = useRef(null);

  const moveCard = (type) => {
    if (isSliding) return;

    const nextIndex =
      type === "next"
        ? (activeCard + 1) % cards.length
        : (activeCard - 1 + cards.length) % cards.length;

    setDirection(type);
    setIncomingCard(nextIndex);
    setIsSliding(true);

    timerRef.current = setTimeout(() => {
      setActiveCard(nextIndex);
      setIncomingCard(null);
      setIsSliding(false);
    }, 650);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const getAnimationClass = (type) => {
    if (!isSliding) return "";

    if (type === "current") {
      return direction === "next"
        ? "animate-[cardCurrentToLeft_650ms_cubic-bezier(0.22,1,0.36,1)_forwards]"
        : "animate-[cardCurrentToRight_650ms_cubic-bezier(0.22,1,0.36,1)_forwards]";
    }

    return direction === "next"
      ? "animate-[cardIncomingFromRight_650ms_cubic-bezier(0.22,1,0.36,1)_forwards]"
      : "animate-[cardIncomingFromLeft_650ms_cubic-bezier(0.22,1,0.36,1)_forwards]";
  };

  return (
    <div className="relative h-[185px] overflow-hidden md:hidden">
      <div
        className={`absolute inset-0 px-8 will-change-transform ${getAnimationClass(
          "current"
        )}`}
      >
        <TextCard
          title={cards[activeCard].title}
          text={cards[activeCard].text}
          Icon={cards[activeCard].Icon}
        />
      </div>

      {incomingCard !== null && (
        <div
          className={`absolute inset-0 px-8 will-change-transform ${getAnimationClass(
            "incoming"
          )}`}
        >
          <TextCard
            title={cards[incomingCard].title}
            text={cards[incomingCard].text}
            Icon={cards[incomingCard].Icon}
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => moveCard("prev")}
        className="absolute left-0 top-1/2 z-10 flex h-10 w-8 -translate-y-1/2 items-center justify-center border-0 bg-transparent text-[#8a8a8a] transition-colors duration-300 hover:text-[#f97316]"
        aria-label="Previous card"
      >
        <span className="block h-[18px] w-[18px] rotate-45 border-b-[3px] border-l-[3px] border-current"></span>
      </button>

      <button
        type="button"
        onClick={() => moveCard("next")}
        className="absolute right-0 top-1/2 z-10 flex h-10 w-8 -translate-y-1/2 items-center justify-center border-0 bg-transparent text-[#8a8a8a] transition-colors duration-300 hover:text-[#f97316]"
        aria-label="Next card"
      >
        <span className="block h-[18px] w-[18px] -rotate-45 border-b-[3px] border-r-[3px] border-current"></span>
      </button>
    </div>
  );
};

const MobileImageSlider = ({ images }) => {
  const [activeImage, setActiveImage] = useState(0);

  useEffect(() => {
    if (!images.length) return;

    const timer = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % images.length);
    }, 3000);

    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="sm:hidden">
      <div className="mx-auto w-full max-w-[300px] overflow-hidden rounded-[18px]">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${activeImage * 100}%)` }}
        >
          {images.map((item, index) => (
            <div key={index} className="w-full shrink-0 px-1">
              <WhyImageCard item={item} />
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 flex items-center justify-center gap-2">
        {images.map((_, index) => (
          <button
            key={index}
            type="button"
            onClick={() => setActiveImage(index)}
            className={`h-2.5 rounded-full transition-all duration-300 ${
              activeImage === index
                ? "w-8 bg-[#f97316]"
                : "w-2.5 bg-[#d1d5db] hover:bg-[#f97316]/60"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

const Home = () => {
  const whyBuyImages = [
    {
      src: choose1,
      alt: "Rush Patch",
      title: "Fast Patch Production",
      description:
        "Quick turnaround with clean finishing and reliable patch quality.",
    },
    {
      src: choose2,
      alt: "Rush Patch Keychain",
      title: "Custom Patch Options",
      description:
        "Choose styles, shapes, colors, and backing options for your design.",
    },
    {
      src: choose3,
      alt: "Emergency Services Patch",
      title: "Professional Quality",
      description:
        "Sharp details, durable stitching, and premium materials for every patch.",
    },
  ];

  const topCards = [
    {
      title: "At Patch Designers",
      text: "At Patch Designers, we are not just makers - we are Custom Patch Designers who bring your ideas to life.",
      Icon: Clock3,
    },
    {
      title: "Professional & Passionate",
      text: "We make it professional and passionate, and we come up with sharp patches which are durable and at the same time what you imagined them to be.",
      Icon: ShieldCheck,
    },
    {
      title: "Best Patch Designers USA",
      text: "Our reputation as one of the Best patch designers USA comes from years of creating stunning patches.",
      Icon: Paperclip,
    },
  ];

  const bottomCards = [
    {
      title: "Sports teams and clubs",
      text: "Sports teams and clubs",
      Icon: Shapes,
    },
    {
      title: "School uniforms, military uniforms and work uniforms.",
      text: "School uniforms, military uniforms and work uniforms.",
      Icon: BadgeCheck,
    },
    {
      title: "Order Online Anywhere",
      text: "With Patch Designers USA online, you can conveniently order from anywhere in the country.",
      Icon: Layers3,
    },
  ];

  const serveItems = [
    "Sports teams and clubs",
    "School uniforms, military uniforms and work uniforms.",
    "One-of-a-kind clothing and fashion labels.",
    "Company logo and advertising products.",
    "Celebrations, events and special presents.",
  ];

  return (
    <>
      <style>
        {`
          @keyframes cardCurrentToLeft {
            0% { transform: translateX(0); opacity: 1; }
            100% { transform: translateX(-100%); opacity: 0.8; }
          }

          @keyframes cardIncomingFromRight {
            0% { transform: translateX(100%); opacity: 0.8; }
            100% { transform: translateX(0); opacity: 1; }
          }

          @keyframes cardCurrentToRight {
            0% { transform: translateX(0); opacity: 1; }
            100% { transform: translateX(100%); opacity: 0.8; }
          }

          @keyframes cardIncomingFromLeft {
            0% { transform: translateX(-100%); opacity: 0.8; }
            100% { transform: translateX(0); opacity: 1; }
          }
        `}
      </style>

      <Hero />

      <SEO
        title="Patch Designers"
        description="Patch Designers"
        keywords="Patch Designers"
      />

      <ProductGrid categoryId="all" categoryName="All Products" limit={6} />

      <section className="w-full bg-white py-10 sm:py-12 lg:py-14">
        <div className="mx-auto w-full max-w-[1600px] px-4 sm:px-6 lg:px-10">
          <div className="title-wrap relative inline-block w-full px-0 lg:px-20 scroll-trigger animate--slide-in">
            <div className="mb-8 text-center sm:mb-10">
              <h2 className="mx-auto max-w-[900px] text-center text-xl font-bold leading-snug tracking-wide text-[#2b2b2b] sm:text-2xl md:text-[28px] lg:text-3xl">
  Why Choose Patch Designers - The Heart of Patch Designers USA
  Wholesale & Online Service
</h2>

              <div className="mx-auto mt-6 h-px w-full max-w-[620px] bg-[#9ca3af]"></div>
            </div>
          </div>

          <div className="hidden sm:block">
            <div className="mx-auto grid max-w-[1080px] grid-cols-3 gap-5 lg:gap-6">
              {whyBuyImages.map((item, index) => (
                <WhyImageCard key={index} item={item} />
              ))}
            </div>
          </div>

          <MobileImageSlider images={whyBuyImages} />

          <div className="mx-auto mt-9 grid max-w-[1180px] grid-cols-1 gap-6 lg:grid-cols-[1.15fr_0.85fr]">
            <div className="rounded-[24px] border border-[#e5eaf0] bg-[#f8fafc] p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7 lg:p-8">
              <p className="text-[15px] font-medium leading-[27px] text-[#415164] sm:text-[16px] sm:leading-[29px]">
                At Patch Designers, we are not just makers - we are Custom Patch
                Designers who bring your ideas to life. We make it professional
                and passionate, and we come up with sharp patches which are
                durable and at the same time what you imagined them to be.
              </p>

              <p className="mt-5 text-[15px] font-medium leading-[27px] text-[#415164] sm:text-[16px] sm:leading-[29px]">
                Since the traditional embroidery and the latest weaved or PVC
                features, we customize each design to fit your requirements.
                With Patch Designers USA online, you can conveniently order from
                anywhere in the country.
              </p>
            </div>

            <div className="rounded-[24px] border border-[#e5eaf0] bg-white p-5 shadow-[0_12px_35px_rgba(15,35,54,0.08)] sm:p-7 lg:p-8">
              <p className="m-0 text-[15px] font-normal leading-[24px] text-[#4a4a4a] sm:text-[16px]">
  Our reputation as one of the Best patch designers USA comes from
                years of creating stunning patches that serve: 
</p>

              <ul className="mt-5 space-y-3">
                {serveItems.map((item, index) => (
                  <li
                    key={index}
                    className="flex gap-3 text-[14px] font-semibold leading-[23px] text-[#415164] sm:text-[15px]"
                  >
                    <span className="mt-[2px] flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#fff7ed] text-[12px] font-extrabold text-[#f97316]">
                      
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-8 hidden grid-cols-3 gap-4 md:grid lg:mt-9 lg:gap-6">
            {topCards.map((card, index) => (
              <TextCard
                key={index}
                title={card.title}
                text={card.text}
                Icon={card.Icon}
              />
            ))}
          </div>

          <div className="mt-5 hidden grid-cols-3 gap-4 md:grid lg:gap-6">
            {bottomCards.map((card, index) => (
              <TextCard
                key={index}
                title={card.title}
                text={card.text}
                Icon={card.Icon}
              />
            ))}
          </div>

          <div className="mt-8 block md:hidden">
            <MobileCardSlider cards={topCards} />
          </div>

          <div className="mt-5 block md:hidden">
            <MobileCardSlider cards={bottomCards} />
          </div>
        </div>
      </section>

      <Featured />

      <WhyBuySection />
    </>
  );
};

export default Home;